import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { SEO } from './SEO';
import { Mail, Clock, AlertCircle, CheckCircle, Send, Phone, MapPin, Settings, DollarSign, Headphones, BookOpen, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { getBillingUrl } from '../lib/referral';

export const SupportPage = () => {
  const { t, language } = useLanguage();
  const getLocalizedPath = (path) => (language === "ru" ? "/ru" + path : path);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    department: 'financial',
    subject: '',
    message: '',
    agreement: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          department: formData.department,
          subject: formData.subject,
          message: formData.message
        })
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          department: 'financial',
          subject: '',
          message: '',
          agreement: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      setSubmitStatus('error');
    }
    
    setIsSubmitting(false);
    setTimeout(() => setSubmitStatus(null), 5000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO page="support" />
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-950 via-blue-800 to-blue-950 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-xl mb-6">
              <Headphones className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t('support.hero.title')}
            </h1>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              {t('support.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {/* Technical Support */}
            <div className="bg-white rounded-xl p-6 border-2 border-blue-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Settings className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{t('support.departments.technical.title')}</h3>
              </div>
              <p className="text-gray-600 mb-3">{t('support.departments.technical.description')}</p>
              <div className="flex items-center gap-2 text-sm text-blue-600 font-semibold">
                <Clock className="w-4 h-4" />
                <span>{t('support.departments.technical.hours')}</span>
              </div>
            </div>

            {/* Financial Support */}
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{t('support.departments.financial.title')}</h3>
              </div>
              <p className="text-gray-600 mb-3">{t('support.departments.financial.description')}</p>
              <div className="flex items-center gap-2 text-sm text-gray-600 font-semibold">
                <Clock className="w-4 h-4" />
                <span>{t('support.departments.financial.hours')}</span>
              </div>
            </div>

            {/* Abuse Department */}
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{t('support.departments.abuse.title')}</h3>
              </div>
              <p className="text-gray-600 mb-3">{t('support.departments.abuse.description')}</p>
              <div className="flex items-center gap-2 text-sm text-gray-600 font-semibold">
                <Clock className="w-4 h-4" />
                <span>{t('support.departments.abuse.hours')}</span>
              </div>
            </div>
          </div>

          {/* Pre-ticket: knowledge base + Telegram channel — try these first */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-2xl p-8 mb-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-7 h-7 text-blue-700" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('support.knowledgeBase.title')}</h3>
                <p className="text-gray-700 leading-relaxed mb-5 max-w-2xl">{t('support.knowledgeBase.description')}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to={getLocalizedPath('/wiki')}>
                    <Button className="bg-blue-700 text-white hover:bg-blue-800 font-semibold px-6 py-3 w-full sm:w-auto">
                      <BookOpen className="w-4 h-4 mr-2" />
                      {t('support.knowledgeBase.kbButton')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <a href="https://t.me/NetGridHost" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-white text-blue-700 hover:bg-blue-50 border-2 border-blue-200 font-semibold px-6 py-3 w-full sm:w-auto">
                      <Send className="w-4 h-4 mr-2" />
                      {t('support.knowledgeBase.tgButton')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notice for Clients */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 mb-12">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-3">{t('support.clientNotice.title')}</h3>
                <p className="text-blue-50 leading-relaxed mb-4">
                  {t('support.clientNotice.description')}
                </p>
                <a 
                  href={getBillingUrl('logon', language)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-6 py-3">
                    {t('support.clientNotice.button')}
                  </Button>
                </a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('support.form.title')}</h2>

              {submitStatus === 'success' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-700" />
                  <p className="text-sm text-blue-900">{t('support.form.successMessage')}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('support.form.name')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('support.form.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('support.form.department')}
                  </label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  >
                    <option value="technical">{t('support.departments.technical.title')}</option>
                    <option value="financial">{t('support.departments.financial.title')}</option>
                    <option value="abuse">{t('support.departments.abuse.title')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('support.form.subject')}
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('support.form.message')}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none resize-none"
                  ></textarea>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="agreement"
                    checked={formData.agreement}
                    onChange={handleChange}
                    required
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="text-sm text-gray-600">
                    {t('support.form.agreement')}
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !formData.agreement}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('support.form.sending') : t('support.form.submit')}
                  <Send className="w-5 h-5" />
                </Button>
              </form>
            </div>

            {/* Contact Info & Image */}
            <div className="space-y-8">
              {/* Image */}
              <div className="rounded-2xl overflow-hidden">
                <img 
                  src="/images/locations/unsplash_1556761175-b413da4baf72.jpg" 
                  alt="Technical Support Operator"
                  loading="lazy" className="w-full h-64 object-cover"
                />
              </div>

              {/* Social Media Updates */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border-2 border-blue-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('support.social.title')}</h3>
                <p className="text-base font-semibold text-blue-900 mb-4">{t('support.social.subtitle')}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a 
                    href="https://t.me/NetGridHost" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                  >
                    <Send className="w-5 h-5" />
                    Join Telegram
                  </a>
                </div>
              </div>

              {/* Contact Methods */}
              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('support.contact.title')}</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('support.contact.generalQuestions')}</h4>
                      <a href="mailto:info@netgrid.host" className="text-blue-600 hover:text-blue-700">
                        info@netgrid.host
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Settings className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('support.contact.general')}</h4>
                      <a href="mailto:support@netgrid.host" className="text-blue-600 hover:text-blue-700">
                        support@netgrid.host
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('support.contact.abuse')}</h4>
                      <a href="mailto:abuse-report@netgrid.host" className="text-blue-600 hover:text-blue-700">
                        abuse-report@netgrid.host
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};


